import React, { useState } from "react";
import { Icon } from "../Icon";
import { cn } from "../lib/utils";

export type AlertVariant = "info" | "success" | "warning" | "danger" | "neutral";

interface AlertProps {
  variant?: AlertVariant;
  title?: React.ReactNode;
  /** Eigenes Icon; `null` blendet das Icon aus. Default: variantenspezifisch. */
  icon?: React.ReactNode | null;
  collapsible?: boolean;
  defaultOpen?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Einheitliche Alert/Info-Box auf Basis der semantischen State-Tokens
 * (bg-{v}-soft / text-{v}-fg / border-{v}). Ersetzt die divergenten
 * InfoBox / InfoPanel / GlobalInfoBanner-Implementierungen. Theme-aware
 * ohne dark:-Klassen — die Tokens schalten via [data-theme="dark"] um.
 */
const VARIANT_CLASS: Record<AlertVariant, string> = {
  info: "bg-info-soft text-info-fg border-info",
  success: "bg-success-soft text-success-fg border-success",
  warning: "bg-warning-soft text-warning-fg border-warning",
  danger: "bg-danger-soft text-danger-fg border-danger",
  neutral: "bg-sunken text-content-2 border-line",
};

const DEFAULT_ICON: Record<AlertVariant, React.ReactNode> = {
  info: <Icon name="info" size={18} />,
  success: <Icon name="check" size={18} />,
  warning: <Icon name="info" size={18} />,
  danger: <Icon name="info" size={18} />,
  neutral: <Icon name="info" size={18} />,
};

export const Alert: React.FC<AlertProps> = ({
  variant = "info",
  title,
  icon,
  collapsible = false,
  defaultOpen = true,
  dismissible = false,
  onDismiss,
  className = "",
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const resolvedIcon = icon === undefined ? DEFAULT_ICON[variant] : icon;

  const header = (title || dismissible || collapsible) && (
    <div className="flex items-center gap-2">
      {resolvedIcon && <span className="mt-0.5 shrink-0">{resolvedIcon}</span>}
      {title && <span className="min-w-0 flex-1 font-semibold">{title}</span>}
      {!title && <span className="min-w-0 flex-1" />}
      {collapsible && (
        <Icon name="chevronDown" size={15} className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")} />
      )}
      {dismissible && (
        <button
          type="button"
          aria-label="Schließen"
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
            onDismiss?.();
          }}
          className="shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
        >
          <Icon name="close" size={15} />
        </button>
      )}
    </div>
  );

  const body = children && (open || !collapsible) && (
    <div className={cn("text-sm leading-relaxed", title ? "mt-2" : null)}>{children}</div>
  );

  return (
    <div role="alert" className={cn("rounded-lg border px-4 py-3", VARIANT_CLASS[variant], className)}>
      {collapsible && header ? (
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center text-left">
          {header}
        </button>
      ) : (
        header
      )}
      {/* Ohne Titel: Icon + Body in einer Zeile */}
      {!title && body && (
        <div className="flex items-start gap-2">
          {resolvedIcon && !header && <span className="mt-0.5 shrink-0">{resolvedIcon}</span>}
          <div className="min-w-0 flex-1">{body}</div>
        </div>
      )}
      {title && body}
    </div>
  );
};

export default Alert;
